import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LogoutIcon } from "@signet/ui";
import { api, isMacDesktop, ByNumero, SignetMark, type BoundMe } from "@signet/web-kit";
import { useTopbarHeight } from "./topbar-height.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The frame for an account bound to one record: a creator's own sign-in.

   Not the staff shell with things hidden. It has the mark, who is signed in
   and a way out, and nothing else: no nav, no palette, no assistant, no
   settings and no module backgrounds, because none of those belong to
   somebody outside the company. The page inside is the bound module's.
   ────────────────────────────────────────────────────────────────────────── */

export function BoundShell({ who }: { who: BoundMe }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const frame = useTopbarHeight<HTMLDivElement, HTMLElement>();

  const signOut = async () => {
    await api("/api/auth/logout", { method: "POST", body: {} }).catch(() => {});
    queryClient.clear();
    navigate({ to: "/login", search: { next: undefined } });
  };

  return (
    <div ref={frame.shell} className={`shell signet-bound${isMacDesktop ? " mac-desktop" : ""}`}>
      <header ref={frame.bar} className="topbar signet-topbar signet-bound-bar">
        <span className="topbar-brand" aria-label="Signet">
          <span className="signet-coin"><SignetMark size={26} /></span>
          <span className="topbar-name"><strong>Signet</strong><ByNumero /></span>
        </span>
        <div className="spacer" />
        <span className="signet-bound-who truncate" title={`Signed in as ${who.username}`}>{who.username}</span>
        <button type="button" className="secondary-button compact" onClick={() => void signOut()}>
          <LogoutIcon size={14} /> Sign out
        </button>
      </header>
      <main className="content" id="main">
        <Outlet />
      </main>
    </div>
  );
}
